// src/tiptap/EditorToolbar.jsx
import {
    Box,
    IconButton,
    Tooltip,
    useMediaQuery,
    TextField,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    Select,
    Button,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { useState } from 'react';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import TableChartIcon from '@mui/icons-material/TableChart';
import BorderClearIcon from '@mui/icons-material/BorderClear';

export default function EditorToolbar({ editor }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [openTableDialog, setOpenTableDialog] = useState(false);
    const [rows, setRows] = useState(3);
    const [cols, setCols] = useState(3);

    if (!editor) return null;

    /* ---------- 헬퍼 ---------- */
    const iconStyle = { width: 36, height: 36, p: 0 };
    const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px'];
    const curSize = editor.getAttributes('textStyle').fontSize || '';

    const Btn = (title, handler, IconCmp) => (
        <Tooltip title={title}>
            <IconButton onClick={handler} sx={iconStyle}>
                <IconCmp />
            </IconButton>
        </Tooltip>
    );

    const align = (pos) => () => editor.chain().focus().setTextAlign(pos).run();

    /* ---------- 렌더 ---------- */
    return (
        <>
            {/* 툴바 버튼들 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {/* 폰트 크기 */}
                <TextField
                    select
                    size="small"
                    variant="outlined"
                    value={curSize}
                    onChange={(e) => {
                        const v = e.target.value;
                        v
                            ? editor.chain().focus().setFontSize(v).run()
                            : editor.chain().focus().unsetFontSize().run();
                    }}
                    sx={{ minWidth: 90 }}
                >
                    <MenuItem value="">기본</MenuItem>
                    {fontSizes.map((s) => (
                        <MenuItem key={s} value={s}>
                            {s}
                        </MenuItem>
                    ))}
                </TextField>

                {isMobile && <Box sx={{ flexBasis: '100%' }} />}

                {/* 공통 버튼 */}
                {Btn('굵게', () => editor.chain().focus().toggleBold().run(), FormatBoldIcon)}
                {Btn('왼쪽 정렬', align('left'), FormatAlignLeftIcon)}
                {Btn('가운데 정렬', align('center'), FormatAlignCenterIcon)}
                {Btn('오른쪽 정렬', align('right'), FormatAlignRightIcon)}

                {/* 데스크톱 전용 */}
                {!isMobile && (
                    <>
                        {Btn(
                            '기울임',
                            () => editor.chain().focus().toggleItalic().run(),
                            FormatItalicIcon
                        )}
                        {Btn(
                            '밑줄',
                            () => editor.chain().focus().toggleUnderline().run(),
                            FormatUnderlinedIcon
                        )}
                        {Btn(
                            '취소선',
                            () => editor.chain().focus().toggleStrike().run(),
                            StrikethroughSIcon
                        )}
                        {Btn(
                            '글머리 기호',
                            () => editor.chain().focus().toggleBulletList().run(),
                            FormatListBulletedIcon
                        )}
                        {Btn(
                            '숫자 리스트',
                            () => editor.chain().focus().toggleOrderedList().run(),
                            FormatListNumberedIcon
                        )}
                        {Btn('실행 취소', () => editor.chain().focus().undo().run(), UndoIcon)}
                        {Btn('다시 실행', () => editor.chain().focus().redo().run(), RedoIcon)}

                        {/* 표 삽입 / 삭제 */}
                        {Btn('표 삽입', () => {
                            // 1️⃣ 현재 포커스(커서) 완전히 해제 
                            if (document.activeElement) document.activeElement.blur();

                            // 2️⃣ 한 틱 뒤에 모달 열기 → aria-hidden 적용 전에 포커스가 없음 
                            setTimeout(() => setOpenTableDialog(true), 0);
                        }, TableChartIcon)}
                        {Btn(
                            '표 삭제',
                            () => editor.chain().focus().deleteTable().run(),
                            BorderClearIcon
                        )}
                    </>
                )}
            </Box>

            {/* 표 삽입 모달 */}
            <Dialog
                open={openTableDialog}
                onClose={() => {
                    setOpenTableDialog(false);
                    editor.commands.focus();
                }}
                container={() => document.body}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>표 삽입</DialogTitle>

                <DialogContent sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    {/* 행 */}
                    <FormControl fullWidth sx={{ minWidth: 140 }}>
                        <Select
                            value={rows}
                            onChange={(e) => setRows(e.target.value)}
                            displayEmpty                                // ⚑ 빈 값에도 렌더링
                            renderValue={(v) => `${v || rows}행`}      // ⚑ 표시 형식
                            MenuProps={{ container: () => document.body }}
                        >
                            {[...Array(10)].map((_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                    {i + 1}행
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* 열 */}
                    <FormControl fullWidth sx={{ minWidth: 140 }}>
                        <Select
                            value={cols}
                            onChange={(e) => setCols(e.target.value)}
                            displayEmpty
                            renderValue={(v) => `${v || cols}열`}
                            MenuProps={{ container: () => document.body }}
                        >
                            {[...Array(10)].map((_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>
                                    {i + 1}열
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => { setOpenTableDialog(false); editor.commands.focus(); }}>
                        취소
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            editor
                                .chain()
                                .focus()
                                .insertTable({ rows, cols, withHeaderRow: true })
                                .insertContent('<p></p>')
                                .run();
                            setOpenTableDialog(false);
                            editor.commands.focus();
                        }}
                    >
                        삽입
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
